/**
 * In-memory TTL cache for API responses.
 * Clears automatically when entries expire or when mutations invalidate them.
 * Resets on full page refresh (intentional — stale data is never served across sessions).
 */

const store = new Map();

/**
 * Retrieve a cached value. Returns null if missing or expired.
 * @param {string} key
 * @returns {*|null}
 */
export function cacheGet(key) {
    const entry = store.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
        store.delete(key);
        return null;
    }
    return entry.data;
}

/**
 * Store a value in the cache with a TTL.
 * @param {string} key
 * @param {*} data
 * @param {number} ttlMs - Time to live in milliseconds (default: 30s)
 */
export function cacheSet(key, data, ttlMs = 30_000) {
    store.set(key, { data, expiresAt: Date.now() + ttlMs });
}

/**
 * Remove a single cache entry (call after create/update/delete mutations).
 * @param {string} key
 */
export function cacheInvalidate(key) {
    store.delete(key);
}

/**
 * Remove all cache entries whose key starts with a given prefix.
 * Useful for invalidating a group (e.g. all 'merchants:*' entries).
 * @param {string} prefix
 */
export function cacheInvalidatePrefix(prefix) {
    for (const key of store.keys()) {
        if (key.startsWith(prefix)) {
            store.delete(key);
        }
    }
}

/**
 * Clear the entire cache (e.g. on logout).
 */
export function cacheClear() {
    store.clear();
}

// Cache key constants — single source of truth
export const CACHE_KEYS = {
    DASHBOARD: 'dashboard',
    MERCHANTS: (search = '') => `merchants:${search}`,
    PLATFORMS: 'platforms',
    STORE_HANDLES: 'store_handles',
    SCANNER_WEBSITES: 'scanner_websites',
    REFERENCE_STORES: 'reference_stores',
    USAGE_CHARGES: 'usage_charges',
    USAGE_CHARGES_ANALYTICS: 'usage_charges_analytics',
    HIGH_RISK_EMAILS: 'high_risk_emails',
    // Merchant details — all keyed by shop domain
    MERCHANT_INFO: (shop) => `merchant_info:${shop}`,
    MERCHANT_FILTER: (shop, filterType) => `merchant_filter:${shop}:${filterType}`,
    MERCHANT_ORDERS_COUNT: (shop, fromDate) => `merchant_orders_count:${shop}:${fromDate}`,
    MERCHANT_COUPONS: (shop) => `merchant_coupons:${shop}`,
    MERCHANT_BLOCKED_ORDERS: (shop) => `merchant_blocked_orders:${shop}`,
};

// TTL constants (ms)
export const CACHE_TTL = {
    SHORT: 30_000,   // 30s  — frequently changing data (stores list)
    MEDIUM: 60_000,  // 60s  — dashboard metrics
    LONG: 120_000,   // 120s — reference/config data (platforms, handlers, scanner sites)
};
